import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';

// Import version checker
import { VersionChecker } from './utils/VersionChecker';

// Import authentication
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';

// Import components
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import TeacherDashboard from './components/TeacherDashboard';
import BatchManagement from './components/BatchManagement';
import SubjectManagement from './components/SubjectManagement';
import EmployeesManagement from './components/EmployeesManagement';
import ClassroomManagement from './components/ClassroomManagement';
import TimetableGenerator from './components/TimetableGenerator';
// import BatchWiseTimetable from './components/BatchWiseTimetable';
import EnhancedTimetable from './components/EnhancedTimetable';
import PayrollManagement from './components/PayrollManagement';
import MySalarySlips from './components/MySalarySlips';
import MyLeaves from './components/MyLeaves';
import ChangePassword from './components/ChangePassword';
import TimeSlotsManagement from './components/TimeSlotsManagement';
import SmartTimetableBuilder from './components/SmartTimetableBuilder';
import SessionManagement from './components/SessionManagement';
import HolidayManagement from './components/HolidayManagement';
import AdminPanel from './components/AdminPanel';
import ExpenseManagement from './components/ExpenseManagement';
import DataCleanup from './components/DataCleanup';
import AdminPermissions from './components/AdminPermissions';
import ActivityLogs from './components/ActivityLogs';
import BackupManagement from './components/BackupManagement';
import UserManual from './components/UserManual';
import TasksPage from './components/TasksPage';
import Help from './components/Help';
import ExamTimetable from './components/ExamTimetable';
import Footer from './components/Footer';
import Toaster from './components/Toaster';

// Main App Layout Component
const AppLayout = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex flex-col">
      <div className="flex flex-1">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              
              {/* Admin-only routes */}
              <Route path="/admin-panel" element={
                <ProtectedRoute adminOnly={true}>
                  <AdminPanel />
                </ProtectedRoute>
              } />
              <Route path="/batches" element={
                <ProtectedRoute adminOnly={true}>
                  <BatchManagement />
                </ProtectedRoute>
              } />
              <Route path="/subjects" element={
                <ProtectedRoute adminOnly={true}>
                  <SubjectManagement />
                </ProtectedRoute>
              } />
              <Route path="/classrooms" element={
                <ProtectedRoute adminOnly={true}>
                  <ClassroomManagement />
                </ProtectedRoute>
              } />
              <Route path="/sessions" element={
                <ProtectedRoute adminOnly={true}>
                  <SessionManagement />
                </ProtectedRoute>
              } />
              <Route path="/holidays" element={
                <ProtectedRoute adminOnly={true}>
                  <HolidayManagement />
                </ProtectedRoute>
              } />
              <Route path="/expense-management" element={
                <ProtectedRoute superAdminOnly={true}>
                  <ExpenseManagement />
                </ProtectedRoute>
              } />

              
              {/* Shared routes (Admin + Teacher) */}
              <Route path="/teacher-dashboard" element={
                <ProtectedRoute>
                  <TeacherDashboard />
                </ProtectedRoute>
              } />
              <Route path="/smart-builder" element={
                <ProtectedRoute>
                  <SmartTimetableBuilder />
                </ProtectedRoute>
              } />
              {/* Members now managed through Admin Panel */}
              <Route path="/smart-timetables" element={
                <ProtectedRoute>
                  <EnhancedTimetable />
                </ProtectedRoute>
              } />
              {/* Legacy route redirects */}
              <Route path="/batch-timetable" element={<Navigate to="/smart-timetables" replace />} />
              <Route path="/timetables" element={<Navigate to="/smart-timetables" replace />} />
              <Route path="/enhanced-timetables" element={<Navigate to="/smart-timetables" replace />} />
              
              {/* Help & Instructions - Available to all authenticated users */}
              <Route path="/help" element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              } />
              
              <Route path="/payroll-management" element={
                <ProtectedRoute adminOnly={true}>
                  <PayrollManagement />
                </ProtectedRoute>
              } />
              <Route path="/my-salary-slips" element={
                <ProtectedRoute>
                  <MySalarySlips />
                </ProtectedRoute>
              } />
              <Route path="/my-leaves" element={
                <ProtectedRoute>
                  <MyLeaves />
                </ProtectedRoute>
              } />
              <Route path="/employees-management" element={
                <ProtectedRoute adminOnly={true}>
                  <EmployeesManagement />
                </ProtectedRoute>
              } />
              <Route path="/change-password" element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              } />
              <Route path="/time-slots" element={
                <ProtectedRoute adminOnly={true}>
                  <TimeSlotsManagement />
                </ProtectedRoute>
              } />
              <Route path="/data-cleanup" element={
                <ProtectedRoute adminOnly={true}>
                  <DataCleanup />
                </ProtectedRoute>
              } />
              <Route path="/admin-permissions" element={
                <ProtectedRoute superAdminOnly={true}>
                  <AdminPermissions />
                </ProtectedRoute>
              } />
              <Route path="/activity-logs" element={
                <ProtectedRoute superAdminOnly={true}>
                  <ActivityLogs />
                </ProtectedRoute>
              } />
              <Route path="/backup-management" element={
                <ProtectedRoute superAdminOnly={true}>
                  <BackupManagement />
                </ProtectedRoute>
              } />
              <Route path="/user-manual" element={
                <ProtectedRoute>
                  <UserManual />
                </ProtectedRoute>
              } />
              <Route path="/tasks" element={
                <ProtectedRoute adminOnly={true}>
                  <TasksPage />
                </ProtectedRoute>
              } />
              <Route path="/exam-timetable" element={
                <ProtectedRoute>
                  <ExamTimetable />
                </ProtectedRoute>
              } />
            </Routes>
          </div>
          
          {/* Footer */}
          <Footer />
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          
          {/* Protected app routes */}
          <Route path="/*" element={<AppLayout />} />
        </Routes>
        <VersionChecker />
        <Toaster />
      </AuthProvider>
    </Router>
  );
}

export default App;
// Build version for cache busting - DO NOT REMOVE
